import {ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, input} from "@angular/core";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {FormControl, ReactiveFormsModule} from '@angular/forms';
import {MatChipListbox, MatChipOption} from '@angular/material/chips';
import {EntitetType, SøkEntitetType} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {i18n, Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {map} from "rxjs/operators";
import {erLikeListerIgnorerRekkefølge} from "felles-ng2/støttefunksjoner/lister";
import {takeUntilDestroyed, toObservable} from "@angular/core/rxjs-interop";
import {filter, startWith, switchMap} from "rxjs";
import {EntitetikonKomponent} from "web-ng2/delt/komponenter/entitetikon/entitetikon";
import {alleEnuminnslag} from "felles-ng2/støttefunksjoner/enumer";
import {TranslateModule} from "@ngx-translate/core";
import {MatTooltip} from "@angular/material/tooltip";
import {standard} from "felles-ng2/støttefunksjoner/nilsjekk";

const debug = initDebugger('VelgSøkbareEntiteterKomponent');

/**
 * Felt for å velge hvilke entiteter man skal søke blandt
 */
@Component({
    selector: 'velg-soekbare-entiteter',
    templateUrl: 'velg-søkbare-entiteter.html',
    styleUrl: 'velg-søkbare-entiteter.scss',
    imports: [EntitetikonKomponent, TranslateModule, MatTooltip, MatChipListbox, ReactiveFormsModule, MatChipOption],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class VelgSøkbareEntiteterKomponent {

    skjemakontroll = input.required<FormControl<SøkEntitetType[]>>();
    /** typer som skal være tilgjengelig for valg i gui */
    tilgjengeligeTyper = input<SøkEntitetType[]>(alleEnuminnslag(SøkEntitetType));

    //pga en bug i mat-chip-listbox (https://github.com/angular/components/issues/27370, rettet i angular 19) må vi sette initiell verdi til null og ikke []
    //da MatChipListbox.ngAfterContentInit skriver initiell oppdaterer gjeldende verdi til initiell verdi ([]) i et promise som resolver etter at
    //vår this.valgte.setValue(entiteter) har blitt kalt, dette skjer ikke dersom vi setter initiell verdi til null
    valgte = new FormControl<Entitet[] | null>(null, {nonNullable: true})
    /** Entiteter tilgjengelig for valg i gui */
    readonly tilgjengeligeEntiteter = computed(() => this.alleTilgjengeligeEntiteter().filter(kandidat => this.tilgjengeligeTyper().includes(kandidat.type)));

    constructor(private oversettelser: Oversettelser, cdRef: ChangeDetectorRef) {
        //oppdater våre valgteEntiteter ihht endringer i angitt skjemakontroll
        toObservable(this.skjemakontroll)
          .pipe(
            //lever ved endringer i skjema, start med initiell leveranse av nåværende skjemaverdi
            switchMap(skjema => skjema.valueChanges.pipe(startWith(skjema.value))),
            //ikke oppdater valgteEntiteter dersom denne allerede er lik verdien til skjemakontrollen (for å unngå rekursjon)
            filter(valgteTyper => !erLikeListerIgnorerRekkefølge(valgteTyper, standard(this.valgte.value, []).map(entitet => entitet.type))),
            map(valgteTyper => this.tilgjengeligeEntiteter().filter(kandidat => valgteTyper.includes(kandidat.type))))
          .subscribe(entiteter => this.valgte.setValue(entiteter));

        //oppdater skjemakontroll ved endringer i valgteEntiteter
        this.valgte.valueChanges
          .pipe(
            map(entiteter => standard(entiteter, []).map(entitet => entitet.type)),
            //ikke oppdater valgteEntiteter dersom denne allerede er lik verdien til skjemakontrollen (for å unngå rekursjon)
            filter(valgteTyper => !erLikeListerIgnorerRekkefølge(valgteTyper, this.skjemakontroll().value)),
            takeUntilDestroyed())
          .subscribe(valgteTyper => this.skjemakontroll().setValue(valgteTyper));
    }

    erValgt(a: Entitet, b: Entitet): boolean {
        return a.type === b.type;
    }

    // private hjelpemetoder

    private alleTilgjengeligeEntiteter(): Entitet[] {
        return [
            {type: SøkEntitetType.møte, entitetType: EntitetType.møte, navn: this.oversettelser.oversett(i18n('Møte')), hjelpetekst: this.oversettelser.oversett(i18n('Inkluder treff på møter'))},
            {type: SøkEntitetType.sak, entitetType: EntitetType.sak, navn: this.oversettelser.oversett(i18n('Sak')), hjelpetekst: this.oversettelser.oversett(i18n('Inkluder treff på saker'))},
            {type: SøkEntitetType.dokument, entitetType: EntitetType.dokument, navn: this.oversettelser.oversett(i18n('Dokument')), hjelpetekst: this.oversettelser.oversett(i18n('Inkluder treff på dokumenter'))},
            {type: SøkEntitetType.oppgave, entitetType: EntitetType.oppgave, navn: this.oversettelser.oversett(i18n('Oppgave')), hjelpetekst: this.oversettelser.oversett(i18n('Inkluder treff på oppgaver'))},
            {type: SøkEntitetType.beslutning, entitetType: EntitetType.beslutning, navn: this.oversettelser.oversett(i18n('Beslutning')), hjelpetekst: this.oversettelser.oversett(i18n('Inkluder treff på beslutninger'))},
            {type: SøkEntitetType.kommentar, entitetType: EntitetType.kommentar, navn: this.oversettelser.oversett(i18n('Kommentar')), hjelpetekst: this.oversettelser.oversett(i18n('Inkluder treff på kommentarer'))},
            {type: SøkEntitetType.gruppe, entitetType: EntitetType.gruppe, navn: this.oversettelser.oversett(i18n('Gruppe')), hjelpetekst: this.oversettelser.oversett(i18n('Inkluder treff på gruppeer'))},
            {type: SøkEntitetType.medlem, entitetType: EntitetType.medlem, navn: this.oversettelser.oversett(i18n('Medlem')), hjelpetekst: this.oversettelser.oversett(i18n('Inkluder treff på medlemer'))},
            {type: SøkEntitetType.bruker, entitetType: EntitetType.bruker, navn: this.oversettelser.oversett(i18n('Bruker')), hjelpetekst: this.oversettelser.oversett(i18n('Inkluder treff på brukere'))},
        ]
    }
}

type Entitet = { type: SøkEntitetType; entitetType: EntitetType; navn: string; hjelpetekst: string; };


/* versjon basert på at bruker legger til / fjerner chips, kun valgte chips vises
@Component({
    selector: 'velg-soekbare-entiteter',
    templateUrl: 'velg-søkbare-entiteter.html',
    styleUrl: 'velg-søkbare-entiteter.scss',
    imports: [MatFormFieldModule, MatChipsModule, MatIconModule, MatAutocompleteModule, FormsModule, ReactiveFormsModule, EntitetikonKomponent, TranslateModule],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class VelgSøkbareEntiteterKomponent {

    skjemakontroll = input.required<FormControl<SøkEntitetType[]>>();
    /!** typer som skal være tilgjengelig for valg i gui *!/
    tilgjengeligeTyper = input<SøkEntitetType[]>(alleEnuminnslag(SøkEntitetType));

    /!** Når bruker trykker på en av disse tastene på tastatur kalles leggTil() med teksten som måtte være angitt i tekstfilter-feltet *!/
    readonly tasterSomTriggerLeggTil: number[] = [ENTER, COMMA];
    /!** Knyttet til inputfelt som lar bruker skrive inn navn på entitet og velge fra mat-autocomplete-liste.
     * Vil når bruker velger innslag fra mat-autocomplete-liste være satt til en Entitet, ellers være satt til teksten bruker skriver inn *!/
    readonly tekstfilter = new FormControl<Entitet | string | null>(null);
    /!** Entiteter tilgjengelig for valg i gui *!/
    private readonly tilgjengeligeEntiteter = computed(() => this.alleTilgjengeligeEntiteter().filter(kandidat => this.tilgjengeligeTyper().includes(kandidat.type)));
    /!** Entiteter valgt av bruker, vises som chips i gui *!/
    readonly valgteEntiteter = signal([]);
    readonly ikkeValgteEntiteter = computed(() => disjunksjon(this.tilgjengeligeEntiteter(), this.valgteEntiteter()));
    /!** Entiteter som kan legges til (en filtrert versjon av ikkeValgteEntiteter) *!/
    readonly entiteterForValg: Signal<Entitet[]>;

    constructor(private oversettelser: Oversettelser) {
        //tekstfilter som signal, utelater leveranser når tekstfilter er satt til Node
        const tekstfilterSomSignal = toSignal(this.tekstfilter.valueChanges
          .pipe(
            filter(tekstEllerEntitet => erStreng(tekstEllerEntitet)),
            map(tekst => tekst.trim().toLowerCase())));
        //entiteterForValg som beregnet verdi med alle ikke-valgte entiteter
        this.entiteterForValg = computed(() =>
          erIkkeTomStreng(tekstfilterSomSignal()) ?
            this.ikkeValgteEntiteter().filter(entitet => entitet.navn.toLowerCase().includes(tekstfilterSomSignal())) :
            this.ikkeValgteEntiteter()
        );

        //oppdater våre valgteEntiteter ihht endringer i angitt skjemakontroll
        toObservable(this.skjemakontroll)
          .pipe(
            //lever ved endringer i skjema, start med initiell levranse av nåværende skjemaverdi
            switchMap(skjema => skjema.valueChanges.pipe(startWith(skjema.value))),
            //ikke oppdater valgteEntiteter dersom denne allerde er lik verdien til skjemakontrollen (for å unngå rekursjon)
            filter(valgteEntiteter => !erLikeListerIgnorerRekkefølge(valgteEntiteter, this.valgteEntiteter().map(entitet => entitet.type))),
          )
          .subscribe(valgteEntiteter => this.valgteEntiteter.set(this.tilgjengeligeEntiteter().filter(kandidat => valgteEntiteter.includes(kandidat.type))));
    }

    /!** Kalles når bruker trykker enter uten å velge innslag fra mat-autokomplete-listen *!/
    leggTil(event: MatChipInputEvent): void {
        const tekstverdi = (event.value || '').trim().toLowerCase();
        if (erTomEllerNil(tekstverdi)) return;

        const valgtEntitet = this.tilgjengeligeEntiteter().find(kandidat => kandidat.navn.toLowerCase().includes(tekstverdi));
        if (erNil(valgtEntitet)) return
        this.leggTilValgtEntitet(valgtEntitet);
    }

    /!** Kalles når krysser bort en chip fra listen over valgte chiper *!/
    slett(slettetEntitet: Entitet): void {
        this.valgteEntiteter.update(entiteter => entiteter.filter(kandidat => kandidat !== slettetEntitet));
        this.oppdaterSkjemakontrollMedValgteEntiteter();
    }

    /!** Kalles når bruker velger et innslag fra mat-autocomplete-listen *!/
    valgt(event: MatAutocompleteSelectedEvent): void {
        const valgtEntitet = event.option.value;
        this.leggTilValgtEntitet(valgtEntitet);
        event.option.deselect();
    }

    // private hjelpemetoder

    private leggTilValgtEntitet(valgtEntitet: Entitet) {
        this.valgteEntiteter.update(entiteter => [...entiteter, valgtEntitet]);
        this.oppdaterSkjemakontrollMedValgteEntiteter();
        this.tekstfilter.setValue('');
    }

    private oppdaterSkjemakontrollMedValgteEntiteter(): void {
        this.skjemakontroll().setValue(this.valgteEntiteter().map(entitet => entitet.type));
    }

    private alleTilgjengeligeEntiteter(): Entitet[] {
        return [
            {type: SøkEntitetType.møte, navn: this.oversettelser.oversett(i18n('Møte')), entitetType: EntitetType.møte},
            {type: SøkEntitetType.sak, navn: this.oversettelser.oversett(i18n('Sak')), entitetType: EntitetType.sak},
            {type: SøkEntitetType.dokument, navn: this.oversettelser.oversett(i18n('Dokument')), entitetType: EntitetType.dokument},
            {type: SøkEntitetType.oppgave, navn: this.oversettelser.oversett(i18n('Oppgave')), entitetType: EntitetType.oppgave},
            {type: SøkEntitetType.beslutning, navn: this.oversettelser.oversett(i18n('Beslutning')), entitetType: EntitetType.beslutning},
            {type: SøkEntitetType.kommentar, navn: this.oversettelser.oversett(i18n('Kommentar')), entitetType: EntitetType.kommentar},
            {type: SøkEntitetType.gruppe, navn: this.oversettelser.oversett(i18n('Gruppe')), entitetType: EntitetType.gruppe},
            {type: SøkEntitetType.medlem, navn: this.oversettelser.oversett(i18n('Medlem')), entitetType: EntitetType.medlem},
            {type: SøkEntitetType.bruker, navn: this.oversettelser.oversett(i18n('Bruker')), entitetType: EntitetType.bruker},
        ]
    }
}

type Entitet = { navn: string; type: SøkEntitetType; entitetType: EntitetType };
*/
